import { useEffect, useState } from "react";
import { X } from "@fg/icons";

/**
 * Minimal app-wide toast ("Task completed — Undo"). A module-level store so
 * any code — hooks, callbacks after navigation — can raise one; <Toaster />
 * is mounted once in the app shell.
 */
export interface ToastAction {
    label: string;
    onClick: () => void;
}

interface ToastItem {
    id: number;
    message: string;
    action?: ToastAction;
}

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<(next: ToastItem[]) => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
    for (const l of listeners) l(items);
}

export function dismissToast(id: number) {
    const t = timers.get(id);
    if (t) clearTimeout(t);
    timers.delete(id);
    items = items.filter((i) => i.id !== id);
    emit();
}

export function showToast(message: string, opts?: { action?: ToastAction; durationMs?: number }): number {
    const id = ++seq;
    items = [...items.slice(-2), { id, message, action: opts?.action }];
    timers.set(id, setTimeout(() => dismissToast(id), opts?.durationMs ?? 5000));
    emit();
    return id;
}

export function Toaster() {
    const [list, setList] = useState<ToastItem[]>(items);
    useEffect(() => {
        listeners.add(setList);
        return () => { listeners.delete(setList); };
    }, []);
    if (list.length === 0) return null;
    return (
        <div
            role="region"
            aria-label="Notifications"
            className="pointer-events-none fixed bottom-14 left-1/2 z-[60] flex w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2"
        >
            {list.map((t) => (
                <div
                    key={t.id}
                    role="status"
                    className="pointer-events-auto flex items-center gap-3 rounded-md bg-foreground px-4 py-2.5 text-sm text-background shadow-lg"
                >
                    <span className="min-w-0 flex-1 truncate">{t.message}</span>
                    {t.action ? (
                        <button
                            type="button"
                            onClick={() => { t.action?.onClick(); dismissToast(t.id); }}
                            className="shrink-0 rounded px-1.5 py-0.5 font-semibold text-primary-foreground underline-offset-2 hover:underline dark:text-primary"
                        >
                            {t.action.label}
                        </button>
                    ) : null}
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={() => dismissToast(t.id)}
                        className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ))}
        </div>
    );
}
